/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Self-contained accordion item.
 *
 * Expects:
 *   - Trigger:     [data-ref="trigger"] with aria-controls pointing to a panel ID
 *   - Panel:       [data-ref="panel"]
 *   - Close icon:  [data-ref="icon-close"]
 *   - Open icon:   [data-ref="icon-open"]
 */
class AccordionItem extends HTMLElement {
  #trigger = null;
  #panel = null;
  #iconClose = null;
  #iconOpen = null;

  connectedCallback() {
    this.#trigger = this.querySelector('[data-ref="trigger"]');
    this.#panel = this.querySelector('[data-ref="panel"]');
    this.#iconClose = this.querySelector('[data-ref="icon-close"]');
    this.#iconOpen = this.querySelector('[data-ref="icon-open"]');

    this.#trigger?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onClick);

    this.#trigger = null;
    this.#panel = null;
    this.#iconClose = null;
    this.#iconOpen = null;
  }

  get #isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#panel.classList.remove('hidden');

    this.#iconClose?.classList.add('hidden');
    this.#iconOpen?.classList.remove('hidden');

    this.closest('accordion-group')?.closeOthers(this);
  }

  close() {
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#panel.classList.add('hidden');

    this.#iconClose?.classList.remove('hidden');
    this.#iconOpen?.classList.add('hidden');
  }

  toggle() {
    this.#isOpen ? this.close() : this.open();
  }

  #onClick = () => this.toggle();
}

if (!customElements.get('accordion-item')) {
  customElements.define('accordion-item', AccordionItem);
}

/**
 * @class AccordionGroup
 * @extends HTMLElement
 *
 * Optional wrapper that coordinates accordion item behavior.
 *
 * Attributes:
 *   - data-mode="multiple" - Multiple items can be open simultaneously (default)
 *   - data-mode="single"   - Only one item open at a time
 */
class AccordionGroup extends HTMLElement {
  get #mode() {
    return this.dataset.mode === 'single' ? 'single' : 'multiple';
  }

  closeOthers(caller) {
    if (this.#mode === 'multiple') return;

    this.querySelectorAll('accordion-item').forEach((item) => {
      if (item !== caller) item.close();
    });
  }
}

if (!customElements.get('accordion-group')) {
  customElements.define('accordion-group', AccordionGroup);
}
